import React, { useMemo, useState } from "react";

import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Switch,
  Table,
  Typography,
} from "antd";

import {
  CheckCircleFilled,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import "./nujobsBuyEmployersPlans.scss";

const { Title, Text } = Typography;

/* ============================================================
   CONFIGURATION
============================================================ */

const EXTRA_JD_PRICE_ABOVE_10 = 149;

const ADDITIONAL_USER_PRICE = 699;

/*
 * Temporary Launch Offer.
 *
 * true:
 *   - NuJobs Launch is available
 *   - Starter / Growth / Business are disabled
 *   - Launch base plan receives 100% discount
 *
 * false:
 *   - Normal plans become selectable
 *   - Launch discount is removed
 */
const LAUNCH_OFFER_ACTIVE = true;

/* ============================================================
   NUJOBS PLANS
============================================================ */
/*
Monthly = 1.00×
Quarterly = 1.40×
Half-Yearly = 1.60×
Yearly = 1.75×
*/
const PLAN_LIST = [
  {
    key: "nujobs_launch",

    planName: "NuJobs Launch",

    description:
      "Special launch offer for employers to start hiring on NuJobs.",

    includedJDs: 50,

    includedUsers: 2,

    pricing: {
      monthly: {
        label: "Monthly",
        duration: "1 Month",
        months: 1,
        price: 6999,
      },

      quarterly: {
        label: "Quarterly",
        duration: "3 Months",
        months: 3,
        price: 11199,
      },

      halfyearly: {
        label: "Half-Yearly",
        duration: "6 Months",
        months: 6,
        price: 21999,
      },

      yearly: {
        label: "Yearly",
        duration: "12 Months",
        months: 12,
        price: 42999,
      },
    },
  },

  {
    key: "starter",

    planName: "Starter",

    description:
      "Best for employers with occasional hiring needs.",

    includedJDs: 1,

    includedUsers: 1,

    pricing: {
      monthly: {
        label: "Monthly",
        duration: "1 Month",
        months: 1,
        price: 299,
      },

      quarterly: {
        label: "Quarterly",
        duration: "3 Months",
        months: 3,
        price: 419,
      },

      halfyearly: {
        label: "Half-Yearly",
        duration: "6 Months",
        months: 6,
        price: 479,
      },

      yearly: {
        label: "Yearly",
        duration: "12 Months",
        months: 12,
        price: 529,
      },
    },
  },

  {
    key: "growth",

    planName: "Growth",

    description:
      "Ideal for companies with regular hiring requirements.",

    includedJDs: 5,

    includedUsers: 1,

    pricing: {
      monthly: {
        label: "Monthly",
        duration: "1 Month",
        months: 1,
        price: 999,
      },

      quarterly: {
        label: "Quarterly",
        duration: "3 Months",
        months: 3,
        price: 1399,
      },

      halfyearly: {
        label: "Half-Yearly",
        duration: "6 Months",
        months: 6,
        price: 1599,
      },

      yearly: {
        label: "Yearly",
        duration: "12 Months",
        months: 12,
        price: 1749,
      },
    },
  },

  {
    key: "business",

    planName: "Business",

    description:
      "Designed for companies with higher hiring volumes.",

    includedJDs: 10,

    includedUsers: 2,

    pricing: {
      monthly: {
        label: "Monthly",
        duration: "1 Month",
        months: 1,
        price: 1799,
      },

      quarterly: {
        label: "Quarterly",
        duration: "3 Months",
        months: 3,
        price: 2519,
      },

      halfyearly: {
        label: "Half-Yearly",
        duration: "6 Months",
        months: 6,
        price: 2879,
      },

      yearly: {
        label: "Yearly",
        duration: "12 Months",
        months: 12,
        price: 3149,
      },
    },
  },
];

/* ============================================================
   STATES
============================================================ */

const STATE_LIST = [
  "Andhra Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Tamil Nadu",
  "Telangana",
  "Uttar Pradesh",
  "West Bengal",
];

/* ============================================================
   HELPERS
============================================================ */

const getPlan = (planKey) => {
  return PLAN_LIST.find(
    (plan) => plan.key === planKey
  );
};

/* ============================================================
   CALCULATE JD PLAN PRICE
============================================================ */

const calculateJDPlanPrice = (
  totalJDs,
  selectedDuration,
  selectedPlanKey
) => {
  const starter = getPlan("starter");
  const growth = getPlan("growth");
  const business = getPlan("business");
  const launch = getPlan("nujobs_launch");

  const starterPrice =
    starter.pricing[selectedDuration].price;

  const growthPrice =
    growth.pricing[selectedDuration].price;

  const businessPrice =
    business.pricing[selectedDuration].price;

  /* ==========================================================
     LAUNCH PLAN

     Launch plan is fixed at 100 JDs.
  ========================================================== */

  if (selectedPlanKey === "nujobs_launch") {
    return launch.pricing[selectedDuration].price;
  }

  /* ==========================================================
     1 JD
  ========================================================== */

  if (totalJDs <= 1) {
    return starterPrice;
  }

  /* ==========================================================
     2 - 5 JDs
  ========================================================== */

  if (totalJDs <= 5) {
    const pricePerAdditionalJD =
      (growthPrice - starterPrice) / 4;

    return (
      starterPrice +
      (totalJDs - 1) *
        pricePerAdditionalJD
    );
  }

  /* ==========================================================
     6 - 10 JDs
  ========================================================== */

  if (totalJDs <= 10) {
    const pricePerAdditionalJD =
      (businessPrice - growthPrice) / 5;

    return (
      growthPrice +
      (totalJDs - 5) *
        pricePerAdditionalJD
    );
  }

  /* ==========================================================
     ABOVE 10 JDs
  ========================================================== */

  return (
    businessPrice +
    (totalJDs - 10) *
      EXTRA_JD_PRICE_ABOVE_10
  );
};

/* ============================================================
   EFFECTIVE PLAN NAME
============================================================ */

const getEffectivePlanName = (
  totalJDs,
  selectedPlanKey
) => {
  if (selectedPlanKey === "nujobs_launch") {
    return "NuJobs Launch";
  }

  if (totalJDs <= 1) {
    return "Starter";
  }

  if (totalJDs < 5) {
    return "Customized Starter";
  }

  if (totalJDs === 5) {
    return "Growth";
  }

  if (totalJDs < 10) {
    return "Customized Growth";
  }

  if (totalJDs === 10) {
    return "Business";
  }

  return "Customized Business";
};

/* ============================================================
   COMPONENT
============================================================ */

export default function NuJobsBuyEmployersPlans() {
  const [form] = Form.useForm();

  /* ==========================================================
     STATE
  ========================================================== */

  const [
    selectedPlanKey,
    setSelectedPlanKey,
  ] = useState(
    LAUNCH_OFFER_ACTIVE
      ? "nujobs_launch"
      : "starter"
  );

  const [
    selectedDuration,
    setSelectedDuration,
  ] = useState("monthly");

  const [
    additionalJDs,
    setAdditionalJDs,
  ] = useState(0);

  const [
    additionalUsers,
    setAdditionalUsers,
  ] = useState(0);

  const [
    gstRegistered,
    setGSTRegistered,
  ] = useState(false);

  /* ==========================================================
     SELECTED PLAN
  ========================================================== */

  const selectedPlan =
    getPlan(selectedPlanKey) ||
    PLAN_LIST[0];

  const isLaunchPlan =
    selectedPlanKey === "nujobs_launch";

  const selectedPricing =
    selectedPlan.pricing[
      selectedDuration
    ];

  /* ==========================================================
     PLAN DISABLED CHECK
  ========================================================== */

  const isPlanDisabled = (planKey) => {
    return (
      LAUNCH_OFFER_ACTIVE &&
      planKey !== "nujobs_launch"
    );
  };

  /* ==========================================================
     TOTAL JDS
  ========================================================== */

  const totalJDs =
    selectedPlan.includedJDs +
    additionalJDs;

  /* ==========================================================
     TOTAL USERS
  ========================================================== */

  const totalUsers =
    selectedPlan.includedUsers +
    additionalUsers;

  /* ==========================================================
     EFFECTIVE PLAN NAME
  ========================================================== */

  const effectivePlanName =
    getEffectivePlanName(
      totalJDs,
      selectedPlanKey
    );

  /* ==========================================================
     CALCULATIONS
  ========================================================== */

  const calculations = useMemo(() => {
    /*
     * Actual commercial value of the
     * selected job posting plan.
     */

    const planAmount =
      calculateJDPlanPrice(
        totalJDs,
        selectedDuration,
        selectedPlanKey
      );

    /*
     * Additional users are NOT included
     * in the launch discount.
     */

    const userAmount =
      additionalUsers *
      ADDITIONAL_USER_PRICE;

    /*
     * 100% discount only on the base
     * NuJobs Launch plan.
     */

    const discountAmount =
      LAUNCH_OFFER_ACTIVE &&
      selectedPlanKey ===
        "nujobs_launch"
        ? planAmount
        : 0;

    const subtotal =
      planAmount +
      userAmount -
      discountAmount;

    const gstAmount =
      subtotal * 0.18;

    const totalAmount =
      subtotal +
      gstAmount;

    return {
      planAmount,
      userAmount,
      discountAmount,
      subtotal,
      gstAmount,
      totalAmount,
    };
  }, [
    totalJDs,
    selectedDuration,
    selectedPlanKey,
    additionalUsers,
  ]);

  /* ==========================================================
     CURRENCY
  ========================================================== */

  const formatCurrency = (
    amount
  ) =>
    new Intl.NumberFormat(
      "en-IN",
      {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    ).format(amount);

  /* ==========================================================
     PLAN CHANGE
  ========================================================== */

  const handlePlanChange = (
    planKey
  ) => {
    /*
     * Do not allow disabled plans
     * to be selected.
     */

    if (
      isPlanDisabled(planKey)
    ) {
      return;
    }

    setSelectedPlanKey(
      planKey
    );

    /*
     * Reset customization when
     * base plan changes.
     */

    setAdditionalJDs(0);
    setAdditionalUsers(0);
  };

  /* ==========================================================
     ADD JD
  ========================================================== */

  const handleAddJD = () => {
    /*
     * Launch is fixed at 100 JDs.
     */

    if (isLaunchPlan) {
      return;
    }

    setAdditionalJDs(
      (value) =>
        value + 1
    );
  };

  /* ==========================================================
     REMOVE JD
  ========================================================== */

  const handleRemoveJD = () => {
    if (isLaunchPlan) {
      return;
    }

    setAdditionalJDs(
      (value) =>
        Math.max(
          0,
          value - 1
        )
    );
  };

  /* ==========================================================
     ADD USER
  ========================================================== */

  const handleAddUser = () => {
    setAdditionalUsers(
      (value) =>
        value + 1
    );
  };

  /* ==========================================================
     REMOVE USER
  ========================================================== */

  const handleRemoveUser = () => {
    setAdditionalUsers(
      (value) =>
        Math.max(
          0,
          value - 1
        )
    );
  };

  /* ==========================================================
     PROCEED
  ========================================================== */

  const handleProceed =
    async () => {
      try {
        const billingDetails =
          await form.validateFields();

        const payload = {
          /* ==============================================
             BASE PLAN
          ============================================== */

          basePlanKey:
            selectedPlan.key,

          basePlanName:
            selectedPlan.planName,

          baseIncludedJDs:
            selectedPlan.includedJDs,

          baseIncludedUsers:
            selectedPlan.includedUsers,

          /* ==============================================
             EFFECTIVE PLAN
          ============================================== */

          effectivePlanName,

          /* ==============================================
             LAUNCH OFFER
          ============================================== */

          launchOfferApplied:
            LAUNCH_OFFER_ACTIVE &&
            isLaunchPlan,

          /* ==============================================
             VALIDITY
          ============================================== */

          billingPeriod:
            selectedDuration,

          billingPeriodName:
            selectedPricing.label,

          duration:
            selectedPricing.duration,

          durationMonths:
            selectedPricing.months,

          /* ==============================================
             JOB POSTINGS
          ============================================== */

          additionalJDs:
            isLaunchPlan
              ? 0
              : additionalJDs,

          totalJDs,

          /* ==============================================
             USERS
          ============================================== */

          additionalUsers,

          totalUsers,

          /* ==============================================
             BILLING DETAILS
          ============================================== */

          billingDetails,

          gstRegistered,

          /* ==============================================
             FRONTEND CALCULATION

             Backend MUST recalculate all prices
             and validate the launch offer.
          ============================================== */

          billing:
            calculations,
        };

        console.log(
          "NuJobs Purchase Payload:",
          payload
        );

        /*
         ====================================================

         IMPORTANT:

         Backend should independently verify:

         1. Launch offer is active
         2. Selected plan is allowed
         3. Base plan price
         4. Discount
         5. Additional user amount
         6. GST
         7. Final payable amount

         Never trust frontend calculated prices.

         ====================================================
        */
      } catch (error) {
        console.log(
          "Validation failed:",
          error
        );
      }
    };

  /* ==========================================================
     PLAN TABLE COLUMNS
  ========================================================== */

  const columns = [
    {
      title: "",
      width: 55,
      align: "center",

      render: (
        _,
        record
      ) => {
        const disabled =
          isPlanDisabled(
            record.key
          );

        return (
          <Radio
            disabled={
              disabled
            }
            checked={
              selectedPlanKey ===
              record.key
            }
            onChange={() =>
              handlePlanChange(
                record.key
              )
            }
          />
        );
      },
    },

    {
      title: "Plan",

      dataIndex:
        "planName",

      render: (
        value,
        record
      ) => {
        const disabled =
          isPlanDisabled(
            record.key
          );

        return (
          <div className="plan-name">

            <span>
              {value}
            </span>

            {selectedPlanKey ===
              record.key && (

              <span className="selected-label">
                {isLaunchPlan
                  ? "Launch Offer"
                  : "Selected"}
              </span>

            )}

            {disabled && (

              <span className="coming-soon-label">
                Coming Soon
              </span>

            )}

          </div>
        );
      },
    },

    {
      title:
        "Job Postings",

      dataIndex:
        "includedJDs",

      align: "center",

      render: (
        value
      ) => (
        <strong>
          {value}
        </strong>
      ),
    },

    {
      title: "Users",

      dataIndex:
        "includedUsers",

      align: "center",
    },

    {
      title:
        "Starting From",

      align: "right",

      render: (
        _,
        record
      ) => (
        <div>

          <strong>
            {formatCurrency(
              record
                .pricing
                .monthly
                .price
            )}
          </strong>

          <div className="price-period">

            {record.key ===
              "nujobs_launch" &&
            LAUNCH_OFFER_ACTIVE
              ? "100% launch discount"
              : "1 month validity"}

          </div>

        </div>
      ),
    },
  ];

  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <div className="nujobs-buy-plan">

      <div className="buy-plan-container">

        {/* ====================================================
            PAGE HEADER
        ==================================================== */}

        <div className="page-header">

          <Title level={2}>
            Buy / Renew NuJobs Plan
          </Title>

          <Text type="secondary">

            {LAUNCH_OFFER_ACTIVE
              ? "NuJobs Launch is currently available with a special 100% launch discount."
              : "Choose a base plan and customize job postings, users and validity based on your hiring requirements."}

          </Text>

        </div>

        <Row
          gutter={[24, 24]}
          align="top"
        >

          {/* ==================================================
              LEFT SIDE
          ================================================== */}

          <Col
            xs={24}
            lg={16}
          >

            <div className="left-section">

              {/* ==============================================
                  STEP 1
              ============================================== */}

              <Card className="section-card">

                <div className="section-heading">

                  <div className="section-number">
                    1
                  </div>

                  <div>

                    <Title level={4}>
                      Choose Your Plan
                    </Title>

                    <Text type="secondary">

                      {LAUNCH_OFFER_ACTIVE
                        ? "NuJobs Launch is currently available. Regular plans will be available after the launch offer."
                        : "Select a starting plan and customize it based on your hiring requirements."}

                    </Text>

                  </div>

                </div>

                <div className="plan-table-wrapper">

                  <Table
                    columns={
                      columns
                    }
                    dataSource={
                      PLAN_LIST
                    }
                    pagination={
                      false
                    }
                    rowKey="key"
                    size="middle"

                    onRow={(
                      record
                    ) => {
                      const disabled =
                        isPlanDisabled(
                          record.key
                        );

                      return {
                        onClick:
                          () => {
                            if (
                              !disabled
                            ) {
                              handlePlanChange(
                                record.key
                              );
                            }
                          },

                        className: [
                          selectedPlanKey ===
                          record.key
                            ? "selected-plan-row"
                            : "",

                          disabled
                            ? "disabled-plan-row"
                            : "",
                        ]
                          .filter(
                            Boolean
                          )
                          .join(" "),
                      };
                    }}
                  />

                </div>

              </Card>

              {/* ==============================================
                  STEP 2
              ============================================== */}

              <Card className="section-card">

                <div className="section-heading">

                  <div className="section-number">
                    2
                  </div>

                  <div>

                    <Title level={4}>
                      Choose Plan Validity
                    </Title>

                    <Text type="secondary">
                      Your job posting credits
                      can be used anytime during
                      the selected validity
                      period.
                    </Text>

                  </div>

                </div>

                <div className="duration-section">

                  <Radio.Group
                    value={
                      selectedDuration
                    }

                    onChange={(
                      e
                    ) =>
                      setSelectedDuration(
                        e.target.value
                      )
                    }

                    className="duration-options"
                  >

                    {Object.entries(
                      selectedPlan
                        .pricing
                    ).map(
                      ([
                        key,
                        pricing,
                      ]) => (

<Radio.Button
  key={key}
  value={key}
  disabled={key !== "monthly"}
  className="duration-option"
>
  <div className="duration-option-content">

    <span className="duration-name">
      {pricing.label}
    </span>

    <strong>
      {formatCurrency(pricing.price)}
    </strong>

    <small>
      {key === "monthly"
        ? `${pricing.duration} validity`
        : "Coming Soon"}
    </small>

  </div>
</Radio.Button>

                      )
                    )}

                  </Radio.Group>

                </div>

              </Card>

              {/* ==============================================
                  STEP 3
              ============================================== */}

              <Card className="section-card">

                <div className="section-heading">

                  <div className="section-number">
                    3
                  </div>

                  <div>

                    <Title level={4}>
                      Customize Your Plan
                    </Title>

                    <Text type="secondary">

                      {isLaunchPlan
                        ? "NuJobs Launch includes 100 job postings and 2 users. Additional users can be added if required."
                        : "Increase the number of job postings or users based on your hiring requirements."}

                    </Text>

                  </div>

                </div>

                <div className="customize-wrapper">

                  {/* ==========================================
                      JOB POSTINGS
                  ========================================== */}

                  <div className="addon-row">

                    <div className="addon-info">

                      <div className="addon-title">
                        Additional Job Postings
                      </div>

                      <div className="addon-description">

                        {
                          selectedPlan.planName
                        }

                        {" "}includes{" "}

                        <strong>
                          {
                            selectedPlan
                              .includedJDs
                          }
                        </strong>

                        {" "}job posting

                        {
                          selectedPlan
                            .includedJDs >
                          1
                            ? "s"
                            : ""
                        }.

                      </div>

                      <div className="addon-price">

                        {isLaunchPlan
                          ? "Job postings are fixed at 100 for the NuJobs Launch plan."
                          : "Price automatically adjusts based on the total number of job postings."}

                      </div>

                    </div>

                    <div className="addon-control-wrapper">

                      {isLaunchPlan ? (

                        <strong>
                          Fixed at 100 JDs
                        </strong>

                      ) : (

                        <div className="quantity-control">

                          <Button
                            icon={
                              <MinusOutlined />
                            }

                            disabled={
                              additionalJDs ===
                              0
                            }

                            onClick={
                              handleRemoveJD
                            }
                          />

                          <InputNumber
                            min={0}

                            controls={
                              false
                            }

                            value={
                              additionalJDs
                            }

                            onChange={(
                              value
                            ) =>
                              setAdditionalJDs(
                                value ||
                                  0
                              )
                            }
                          />

                          <Button
                            icon={
                              <PlusOutlined />
                            }

                            onClick={
                              handleAddJD
                            }
                          />

                        </div>

                      )}

                    </div>

                  </div>

                  <Divider />

                  {/* ==========================================
                      USERS
                  ========================================== */}

                  <div className="addon-row">

                    <div className="addon-info">

                      <div className="addon-title">
                        Additional Users
                      </div>

                      <div className="addon-description">

                        {
                          selectedPlan.planName
                        }

                        {" "}includes{" "}

                        <strong>
                          {
                            selectedPlan
                              .includedUsers
                          }
                        </strong>

                        {" "}user

                        {
                          selectedPlan
                            .includedUsers >
                          1
                            ? "s"
                            : ""
                        }.

                      </div>

                      <div className="addon-price">

                        {formatCurrency(
                          ADDITIONAL_USER_PRICE
                        )}

                        {
                          " / additional user"
                        }

                      </div>

                    </div>

                    <div className="addon-control-wrapper">

                      <div className="quantity-control">

                        <Button
                          
                          icon={
                            <MinusOutlined />
                          }

                          disabled={
                            additionalUsers ===
                            0
                          }

                          onClick={
                            handleRemoveUser
                          }
                        />

                        <InputNumber
                          min={0}
                          disabled={
                             true
                          }
                          controls={
                            false
                          }

                          value={
                            additionalUsers
                          }

                          onChange={(
                            value
                          ) =>
                            setAdditionalUsers(
                              value ||
                                0
                            )
                          }
                        />

                        <Button
                          icon={
                            <PlusOutlined />
                          }
                          disabled={
                             true
                          }
                          onClick={
                            handleAddUser
                          }
                        />

                      </div>

                    </div>

                  </div>

                </div>

                {/* ============================================
                    CAPACITY
                ============================================ */}

                <div className="plan-capacity">

                  <span>
                    Plan:

                    <strong>
                      {" "}
                      {
                        effectivePlanName
                      }
                    </strong>

                  </span>

                  <span>
                    Total Job Postings:

                    <strong>
                      {" "}
                      {totalJDs}
                    </strong>

                  </span>

                  <span>
                    Total Users:

                    <strong>
                      {" "}
                      {totalUsers}
                    </strong>

                  </span>

                  <span>
                    Validity:

                    <strong>
                      {" "}
                      {
                        selectedPricing
                          .duration
                      }
                    </strong>

                  </span>

                </div>

              </Card>

              {/* ==============================================
                  STEP 4
              ============================================== */}

              <Card className="section-card">

                <div className="section-heading">

                  <div className="section-number">
                    4
                  </div>

                  <div>

                    <Title level={4}>
                      Billing Details
                    </Title>

                    <Text type="secondary">
                      These details will be
                      used for your invoice.
                    </Text>

                  </div>

                </div>

                <Form
                  form={form}
                  layout="vertical"
                  className="billing-form"
                >

                  <Row gutter={16}>

                    <Col
                      xs={24}
                      md={12}
                    >

                      <Form.Item
                        label="Company / Legal Name"
                        name="companyName"

                        rules={[
                          {
                            required:
                              true,

                            message:
                              "Please enter company name",
                          },
                        ]}
                      >

                        <Input placeholder="Company name" />

                      </Form.Item>

                    </Col>

                    <Col
                      xs={24}
                      md={12}
                    >

                      <Form.Item
                        label="Billing Email"
                        name="billingEmail"

                        rules={[
                          {
                            required:
                              true,

                            message:
                              "Please enter billing email",
                          },

                          {
                            type:
                              "email",

                            message:
                              "Please enter a valid email",
                          },
                        ]}
                      >

                        <Input placeholder="accounts@company.com" />

                      </Form.Item>

                    </Col>

                  </Row>

                  <Form.Item
                    label="Billing Address"
                    name="billingAddress"

                    rules={[
                      {
                        required:
                          true,

                        message:
                          "Please enter billing address",
                      },
                    ]}
                  >

                    <Input.TextArea
                      rows={3}
                      placeholder="Enter complete billing address"
                    />

                  </Form.Item>

                  <Row gutter={16}>

                    <Col
                      xs={24}
                      md={8}
                    >

                      <Form.Item
                        label="City"
                        name="city"

                        rules={[
                          {
                            required:
                              true,

                            message:
                              "Enter city",
                          },
                        ]}
                      >

                        <Input placeholder="City" />

                      </Form.Item>

                    </Col>

                    <Col
                      xs={24}
                      md={8}
                    >

                      <Form.Item
                        label="State"
                        name="state"

                        rules={[
                          {
                            required:
                              true,

                            message:
                              "Select state",
                          },
                        ]}
                      >

                        <Select
                          showSearch

                          placeholder="Select state"

                          options={
                            STATE_LIST.map(
                              (
                                state
                              ) => ({
                                label:
                                  state,

                                value:
                                  state,
                              })
                            )
                          }
                        />

                      </Form.Item>

                    </Col>

                    <Col
                      xs={24}
                      md={8}
                    >

                      <Form.Item
                        label="PIN Code"
                        name="pinCode"

                        rules={[
                          {
                            required:
                              true,

                            message:
                              "Enter PIN code",
                          },
                        ]}
                      >

                        <Input
                          maxLength={
                            6
                          }

                          placeholder="PIN Code"
                        />

                      </Form.Item>

                    </Col>

                  </Row>

                  {/* ==========================================
                      GST
                  ========================================== */}

                  <div className="gst-switch-row">

                    <div>

                      <div className="gst-title">
                        GST Registered
                      </div>

                      <Text type="secondary">
                        Enable if your
                        organization has a
                        GSTIN.
                      </Text>

                    </div>

                    <Switch
                      checked={
                        gstRegistered
                      }

                      onChange={
                        setGSTRegistered
                      }
                    />

                  </div>

                  {gstRegistered && (

                    <Row gutter={16}>

                      <Col
                        xs={24}
                        md={12}
                      >

                        <Form.Item
                          label="GSTIN"
                          name="gstin"

                          rules={[
                            {
                              required:
                                true,

                              message:
                                "Enter GSTIN",
                            },
                          ]}
                        >

                          <Input
                            placeholder="GSTIN"

                            maxLength={
                              15
                            }

                            onChange={(
                              e
                            ) => {
                              const value =
                                e.target.value.toUpperCase();

                              form.setFieldsValue(
                                {
                                  gstin:
                                    value,
                                }
                              );
                            }}
                          />

                        </Form.Item>

                      </Col>

                      <Col
                        xs={24}
                        md={12}
                      >

                        <Form.Item
                          label="GST Legal Name"
                          name="gstLegalName"

                          rules={[
                            {
                              required:
                                true,

                              message:
                                "Enter GST legal name",
                            },
                          ]}
                        >

                          <Input placeholder="Legal entity name" />

                        </Form.Item>

                      </Col>

                    </Row>

                  )}

                </Form>

              </Card>

            </div>

          </Col>

          {/* ==================================================
              ORDER SUMMARY
          ================================================== */}

          <Col
            xs={24}
            lg={8}
          >

            <div className="order-summary-wrapper">

              <Card className="order-summary-card">

                <Title level={4}>
                  Order Summary
                </Title>

                {/* ============================================
                    PLAN
                ============================================ */}

                <div className="selected-plan-summary">

                  <CheckCircleFilled />

                  <div>

                    <strong>
                      {
                        effectivePlanName
                      }
                    </strong>

                    <span>

                      {totalJDs} job posting

                      {totalJDs > 1
                        ? "s"
                        : ""}

                      {" · "}

                      {totalUsers} user

                      {totalUsers > 1
                        ? "s"
                        : ""}

                      {" · "}

                      {
                        selectedPricing
                          .duration
                      }{" "}

                      validity

                    </span>

                  </div>

                </div>

                {/* ============================================
                    PLAN AMOUNT
                ============================================ */}

                <div className="summary-row">

                  <span>

                    Job Posting Plan

                    <small>

                      {totalJDs} job posting

                      {totalJDs > 1
                        ? "s"
                        : ""}

                      {" · "}

                      {
                        selectedPricing
                          .duration
                      }

                    </small>

                  </span>

                  <strong>

                    {formatCurrency(
                      calculations
                        .planAmount
                    )}

                  </strong>

                </div>

                {/* ============================================
                    ADDITIONAL USERS
                ============================================ */}

                {additionalUsers >
                  0 && (

                  <div className="summary-row">

                    <span>

                      Additional Users

                      <small>

                        {
                          additionalUsers
                        }{" "}
                        ×{" "}

                        {formatCurrency(
                          ADDITIONAL_USER_PRICE
                        )}

                      </small>

                    </span>

                    <strong>

                      {formatCurrency(
                        calculations
                          .userAmount
                      )}

                    </strong>

                  </div>

                )}

                {/* ============================================
                    LAUNCH DISCOUNT
                ============================================ */}

                {calculations
                  .discountAmount >
                  0 && (

                  <div className="summary-row launch-discount-row">

                    <span>

                      Launch Offer Discount

                      <small>
                        100% discount on
                        NuJobs Launch plan
                      </small>

                    </span>

                    <strong>

                      -
                      {formatCurrency(
                        calculations
                          .discountAmount
                      )}

                    </strong>

                  </div>

                )}

                <Divider />

                {/* ============================================
                    SUBTOTAL
                ============================================ */}

                <div className="summary-row">

                  <span>
                    Subtotal
                  </span>

                  <strong>

                    {formatCurrency(
                      calculations
                        .subtotal
                    )}

                  </strong>

                </div>

                {/* ============================================
                    GST
                ============================================ */}

                <div className="summary-row">

                  <span>
                    GST (18%)
                  </span>

                  <strong>

                    {formatCurrency(
                      calculations
                        .gstAmount
                    )}

                  </strong>

                </div>

                <Divider />

                {/* ============================================
                    TOTAL
                ============================================ */}

                <div className="summary-total">

                  <span>
                    Total Payable
                  </span>

                  <strong>

                    {formatCurrency(
                      calculations
                        .totalAmount
                    )}

                  </strong>

                </div>

                <div className="summary-tax-note">

                  {LAUNCH_OFFER_ACTIVE &&
                  isLaunchPlan &&
                  additionalUsers ===
                    0
                    ? "Your NuJobs Launch plan is fully covered by the launch offer."
                    : "Inclusive of applicable taxes"}

                </div>

                {/* ============================================
                    PROCEED
                ============================================ */}

                <Button
                  type="primary"
                  size="large"
                  block

                  className="proceed-button"

                  onClick={
                    handleProceed
                  }
                >

                  {calculations
                    .totalAmount ===
                  0
                    ? "Trial Plan Active"
                    : "Proceed to Pay"}

                </Button>

                <div className="payment-note">

                  Your{" "}

                  <strong>
                    {totalJDs}
                  </strong>

                  {" "}job posting credits
                  will remain valid for{" "}

                  <strong>
                    {
                      selectedPricing
                        .duration
                    }
                  </strong>

                  .

                  {LAUNCH_OFFER_ACTIVE &&
                    isLaunchPlan && (
                      <>
                        {" "}
                        The base plan is
                        currently covered by
                        the NuJobs Launch
                        Offer.
                      </>
                    )}

                </div>

              </Card>

            </div>

          </Col>

        </Row>

      </div>

    </div>
  );
}